import Vue from 'vue';
import { makeToast } from '@/modules/budget-request/components/js/some-functions';

// Ключи (`${id}_pdf` / `${id}_xlsx`) отчётов, файлы которых скачиваются в данный момент.
// Состояние вынесено из компонента намеренно: вкладка «Пакетная выгрузка» отрисована
// внутри <b-tabs lazy>, поэтому при переходе на другую вкладку компонент уничтожается,
// а само скачивание продолжается — блокировка иконки должна сохраняться до его окончания.
const downloadState = Vue.observable<{ keys: string[] }>({ keys: [] });

function downloadKey(id: number, pdf: boolean): string {
    return `${id}_${pdf ? 'pdf' : 'xlsx'}`;
}

// Перезагрузка или закрытие страницы обрывает запрос, и файл не сохранится, поэтому пока
// идёт хотя бы одно скачивание, показываем стандартное предупреждение браузера (свой текст
// современные браузеры игнорируют — сообщение выводится на языке браузера).
function onBeforeUnload(event: BeforeUnloadEvent) {
    event.preventDefault();
    // легаси-канал для Chrome/Edge < 119 и старых Safari, где preventDefault не срабатывал;
    event.returnValue = true;
}

// Слушатель живёт столько же, сколько скачивание, а не сколько компонент: пользователь может
// уйти с вкладки или вообще на другую страницу, и предупреждение должно остаться работать.
function syncUnloadWarning() {
    if (downloadState.keys.length > 0) {
        // повторная регистрация той же функции игнорируется браузером
        window.addEventListener('beforeunload', onBeforeUnload);
    } else {
        window.removeEventListener('beforeunload', onBeforeUnload);
    }
}

// идёт ли скачивание файла по данному отчёту
export function isFileDownloading(id: number, pdf = false): boolean {
    return downloadState.keys.includes(downloadKey(id, pdf));
}

// помечает начало скачивания; false — скачивание уже идёт, запрос дублировать не нужно
export function startFileDownload(id: number, pdf = false): boolean {
    const key = downloadKey(id, pdf);
    if (downloadState.keys.includes(key)) { return false; }
    downloadState.keys = downloadState.keys.concat(key);
    syncUnloadWarning();
    return true;
}

export function finishFileDownload(id: number, pdf = false) {
    const key = downloadKey(id, pdf);
    downloadState.keys = downloadState.keys.filter((el) => el !== key);
    syncUnloadWarning();
}

// Ответ проверки доступности архива: GET /api/summary/report/archive/{id}
export interface IArchiveInfo {
    available: boolean;
    sizeBytes: number | null;
    fileName: string | null;
}

// Результат проверки: при ok=false в status лежит HTTP-код, в message — текст бэка (если есть)
export interface IArchiveCheckResult {
    ok: boolean;
    status: number;
    message: string;
    // ключ ошибки бэка (I18nTemplate.key), если тело ответа — JSON с ключом
    key: string;
    info: IArchiveInfo | null;
}

// Достаёт из ответа бэка ключ ошибки (I18nTemplate JSON `{ key, args }`) и/или текст
async function readErrorMessage(response: Response): Promise<{ key: string; text: string }> {
    try {
        const text = await response.text();
        if (!text) { return { key: '', text: '' }; }
        try {
            const json = JSON.parse(text);
            return { key: typeof json.key === 'string' ? json.key : '', text: String(json.message || json.error || json.detail || '') };
        } catch (e) {
            return { key: '', text: text.length < 300 ? text : '' };
        }
    } catch (e) {
        return { key: '', text: '' };
    }
}

// Короткая проверка доступности архива перед запуском скачивания браузером.
// Сетевые ошибки (fetch бросает исключение) пробрасываются вызывающему.
export async function checkArchive(id: number): Promise<IArchiveCheckResult> {
    const response = await fetch(`/api/summary/report/archive/${id}`, { credentials: 'same-origin' });
    if (response.status === 200) {
        const info = await response.json() as IArchiveInfo;
        return { ok: true, status: 200, message: '', key: '', info };
    }
    const err = await readErrorMessage(response);
    return { ok: false, status: response.status, message: err.text, key: err.key, info: null };
}

// Передаёт скачивание менеджеру загрузок браузера: скрытая ссылка того же origin
// (авторизация по cookie сессии), страница при этом не перезагружается
export function triggerBrowserDownload(url: string, fileName: string) {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
}

// Размер файла в КБ/МБ/ГБ с учётом локали
export function formatSize(bytes: number, locale = 'ru'): string {
    const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
    if (locale === 'en') { units.splice(0, units.length, 'B', 'KB', 'MB', 'GB', 'TB'); }
    let value = bytes;
    let idx = 0;
    while (value >= 1024 && idx < units.length - 1) {
        value /= 1024;
        idx++;
    }
    const num = new Intl.NumberFormat(locale, { maximumFractionDigits: idx === 0 ? 0 : 1 }).format(value);
    return `${num} ${units[idx]}`;
}

// Порог, выше которого архив не поместится на флешку с FAT32 (4 ГБ)
export const FAT32_LIMIT_BYTES = 4 * 1024 * 1024 * 1024;

export interface IBatchUpParams {
    cur_year: number;
    region: number;
    variant: string;
    abp: number;
    guParent: string | null;
    gkkp?: string | null;
    isGkkpCreate: boolean;
    isAgregateCreate?: boolean;
    isConfirmDoc?: boolean;
    isAdditionalNeed?: boolean;
    lang: string;
    prg: number | null;
    gu?: string | null;
}

export async function startBatchUpload(params: IBatchUpParams, that: any) {
    if (!params.gu) { delete params.gu; }
    if (params.gu === '-1') { params.gu = null; }
    if (params.gkkp === '-1') { params.gkkp = null; }
    if (params.guParent === '-1') { params.guParent = null; }
    if (params.prg === -1) { params.prg = null; }
    try {
        const url = '/api/summary/report/create';
        let response: any = await fetch(url, {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify(params)
        });
        if (response.status!== 200) {
            makeToast(that, 'danger', 'Ошибка загрузки', `${response.status} ${response.statusText}`);
            return;
        }
        response = await response.json();
        makeToast(that, 'success', 'Сообщение', 'Загрузка запущена');
    } catch (error) {
        makeToast(that, 'danger', 'Ошибка загрузки', (error as Error).toString());
    }
}